import { ChangeEvent, PropsWithChildren, useCallback, useId } from 'react';
import styles from './selectableHeader.module.scss';
import { PermissionAware } from 'core';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';

export type SelectableHeaderProps = {
  selectedCount: number;
  allSelected: boolean;
  selectPermissionAware?: PermissionAware;
  onSelectPage?: (checked: boolean) => void;
};

export const SelectableHeader: React.FC<SelectableHeaderProps & PropsWithChildren> = ({
  selectedCount,
  allSelected,
  children,
  selectPermissionAware,
  onSelectPage
}) => {

  // Per-instance ID so multiple tables on the same page (e.g., a selected
  // table with a recommend-panel modal on top) don't share the checkbox
  // element via a duplicate DOM id — clicking the label in one would route
  // the browser to the other's <input>.
  const checkboxId = useId();

  const onCheckboxChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    onSelectPage && onSelectPage(e.target.checked);
  }, [onSelectPage]);

  const renderCheckbox = useCallback(() => (
    <>
      <input
        type='checkbox'
        checked={allSelected}
        ref={el => el && (el.indeterminate = !allSelected && selectedCount > 0)}
        onChange={onCheckboxChange}
        id={checkboxId}
      />
      <label htmlFor={checkboxId} />
    </>
  ), [allSelected, selectedCount, onCheckboxChange, checkboxId]);

  return (
    <div className={styles.selectableHeader} key={allSelected.toString()}>
      {onSelectPage ?
        selectPermissionAware ?
          <PermissionChecker permissionAware={selectPermissionAware}>
            {renderCheckbox()}
          </PermissionChecker> :
          renderCheckbox()
        : null
      }
      {children}
    </div>
  );
};
